import { SdpStatusEntry } from "../api/contractStatus";

/**
 * Whether a commodity SDP has a contract awaiting signature.
 *
 * Until now the Sign / Send-proposal buttons keyed off the status of the
 * contract flattened into the portfolio row, which was the highest-id one. That
 * happened to be the unsigned draft, so the buttons appeared. Once the row shows
 * the contract actually in force (portals-backend #511), the row reads "active"
 * and the buttons would vanish for every SDP with a pending renewal.
 *
 * The signal moves to the status endpoint, which already reports a renewal:
 * an SDP with a current contract AND a future-starting successor.
 *
 * A draft whose start date has already passed is deliberately NOT covered.
 * The trigger classifies it `expired`, and the rule confirmed with the product
 * owner is that a commodity contract is signable only up to its start date —
 * after that the customer gets a fresh preswitch rather than signing a lapsed
 * offer. `renewal` only reports future-starting successors, so it expresses that
 * rule exactly, with no date arithmetic here.
 */
export function needsCommoditySignature(
  entry: SdpStatusEntry | undefined,
  rowStatus: string | null | undefined
): boolean {
  // An SDP whose own contract is unsigned — no active predecessor. The row
  // itself still carries that status, so this keeps working when the status
  // endpoint is unavailable.
  if (rowStatus === "unsigned") return true;

  // An active contract with a future-starting successor that is not yet signed.
  return entry?.renewal != null && entry.renewal.signed === false;
}
